import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TableRestaurantOutlinedIcon from '@mui/icons-material/TableRestaurantOutlined';
import { toast } from 'react-toastify';
import { Prompt } from 'react-router-dom';
import useFloorPlanBuilder from './useFloorPlanBuilder';
import FloorPlanCanvas from './FloorPlanCanvas';
import FloorPlanInspector from './FloorPlanInspector';
import AddTableModal from './AddTableModal';
import AddObjectModal from './AddObjectModal';
import AddSectionModal from './AddSectionModal';
import ArrangeSheet from './ArrangeSheet';
import HallStyleSheet from './HallStyleSheet';
import TableShapeGlyph from './TableShapeGlyph';
import { FP, fpCard } from './lib/ui';

const FloorPlanBuilder = () => {
  const fp = useFloorPlanBuilder();
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [showAddTable, setShowAddTable] = useState(false);
  const [showAddObject, setShowAddObject] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showArrange, setShowArrange] = useState(false);
  const [showHall, setShowHall] = useState(false);
  const [outlineEdit, setOutlineEdit] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (fp.isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [fp.isDirty]);

  const selectedTable = useMemo(
    () =>
      fp.sectionTables.find(
        (t) => t?.tableDetails?.uuid === fp.selectedTableUuid
      ) || null,
    [fp.sectionTables, fp.selectedTableUuid]
  );

  const selectedPlacement = fp.selectedTableUuid
    ? fp.sectionLayout?.tables?.[fp.selectedTableUuid] || null
    : null;

  const selectedObject = fp.selectedObjectId
    ? fp.sectionLayout?.objects?.[fp.selectedObjectId] || null
    : null;

  /** Stretch the logical hall canvas to the workspace (keeps relative table positions). */
  const handleFillToViewport = useCallback(
    ({ width, height }) => {
      fp.updateCanvasSize(width, height, {
        rescale: true,
        recordHistory: false,
      });
    },
    [fp.updateCanvasSize]
  );

  const placedUuids = useMemo(
    () => new Set(Object.keys(fp.sectionLayout?.tables || {})),
    [fp.sectionLayout]
  );

  const unplacedTables = useMemo(
    () =>
      fp.sectionTables.filter(
        (t) => t?.tableDetails?.uuid && !placedUuids.has(t.tableDetails.uuid)
      ),
    [fp.sectionTables, placedUuids]
  );

  const placedCount = Object.keys(fp.sectionLayout?.tables || {}).length;

  if (fp.isBusy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!fp.sections.length) {
    return (
      <Box
        sx={{
          ...fpCard,
          p: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: FP.primarySoft,
            color: FP.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TableRestaurantOutlinedIcon />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: FP.ink }}>
          No sections yet
        </Typography>
        <Typography variant="body2" sx={{ color: FP.sub, maxWidth: 380 }}>
          Add a section to start building your floor plan. Everything you create
          here is saved together when you press Save.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => fp.createSection('Main Dining')}
          sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
        >
          Add section
        </Button>
      </Box>
    );
  }

  const iconBtnSx = {
    color: FP.sub,
    border: `1px solid ${FP.line}`,
    borderRadius: 2,
    '&:hover': { borderColor: FP.faint, bgcolor: FP.surface },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Warn on in-app navigation while there are unsaved floor plan changes. */}
      <Prompt
        when={fp.isDirty}
        message="You have unsaved floor plan changes. Leave without saving?"
      />
      {/* Toolbar */}
      <Box
        sx={{
          ...fpCard,
          p: 1.5,
          boxShadow: FP.shadowSm,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 18,
                  color: FP.ink,
                  lineHeight: 1.1,
                }}
              >
                Floor Plan
              </Typography>
              <Typography sx={{ fontSize: 12, color: FP.sub }}>
                {placedCount} table{placedCount === 1 ? '' : 's'} on floor
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              label={fp.isDirty ? 'Unsaved changes' : 'All changes saved'}
              sx={{
                fontWeight: 700,
                bgcolor: fp.isDirty ? '#FEF3C7' : '#DCFCE7',
                color: fp.isDirty ? '#92400E' : '#166534',
                border: 'none',
              }}
            />
            <Button
              size="small"
              startIcon={<PaletteOutlinedIcon />}
              onClick={() => setShowHall(true)}
              sx={{ textTransform: 'none', color: FP.sub }}
            >
              Floor color
            </Button>
            <Button
              size="small"
              startIcon={<GridViewOutlinedIcon />}
              onClick={() => setShowArrange(true)}
              sx={{ textTransform: 'none', color: FP.sub }}
            >
              Arrange
            </Button>
            <Button
              size="small"
              startIcon={<DeleteSweepOutlinedIcon />}
              onClick={async () => {
                if (
                  window.confirm(
                    'Delete all tables in this section from catalog and floor?'
                  )
                ) {
                  const err = await fp.clearAllTables();
                  if (err) toast.error(err);
                }
              }}
              sx={{ textTransform: 'none', color: FP.danger }}
            >
              Clear all
            </Button>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.5 }}
            />

            <Tooltip title="Undo">
              <span>
                <IconButton
                  size="small"
                  disabled={!fp.canUndo}
                  onClick={fp.undo}
                  sx={iconBtnSx}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo">
              <span>
                <IconButton
                  size="small"
                  disabled={!fp.canRedo}
                  onClick={fp.redo}
                  sx={iconBtnSx}
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Discard all unsaved changes">
              <span>
                <IconButton
                  size="small"
                  disabled={!fp.canDiscardAll}
                  onClick={() => {
                    if (
                      window.confirm('Discard all unsaved floor plan changes?')
                    ) {
                      fp.discardAllChanges();
                    }
                  }}
                  sx={iconBtnSx}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              disabled={!fp.isDirty || fp.isSaving}
              onClick={fp.flushSave}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 2,
                bgcolor: FP.primary,
                boxShadow: '0 6px 16px rgba(45,127,249,0.30)',
                '&:hover': { bgcolor: FP.primaryDark },
              }}
            >
              {fp.isSaving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '264px 1fr 292px' },
          gap: 1.5,
          height: {
            xs: 'auto',
            lg: 'clamp(560px, calc(100vh - 240px), 900px)',
          },
          minHeight: 560,
          alignItems: 'stretch',
        }}
      >
        {/* Left rail */}
        <Box
          sx={{
            ...fpCard,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Section</InputLabel>
            <Select
              label="Section"
              value={fp.selectedSectionUuid || ''}
              onChange={(e) => {
                fp.setSelectedSectionUuid(e.target.value);
                fp.setSelectedTableUuid(null);
              }}
            >
              {fp.sections.map((section) => {
                const uuid = section?.sectionDetails?.uuid;
                const name =
                  section?.sectionDetails?.displayName ||
                  section?.sectionDetails?.name ||
                  'Section';
                return (
                  <MenuItem key={uuid} value={uuid}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowAddSection(true)}
              sx={{ textTransform: 'none', flex: 1 }}
              variant="outlined"
            >
              Section
            </Button>
            <Button
              size="small"
              onClick={() => setOutlineEdit((v) => !v)}
              variant={outlineEdit ? 'contained' : 'outlined'}
              sx={{ textTransform: 'none', flex: 1 }}
            >
              {outlineEdit ? 'Done outline' : 'Edit outline'}
            </Button>
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 11,
                letterSpacing: '0.09em',
                color: FP.faint,
              }}
            >
              NOT ON FLOOR ({unplacedTables.length})
            </Typography>
            {unplacedTables.length > 0 ? (
              <Button
                size="small"
                onClick={() => unplacedTables.forEach(fp.placeCatalogTable)}
                sx={{ textTransform: 'none', minWidth: 0 }}
              >
                Place all
              </Button>
            ) : null}
          </Stack>

          <Stack spacing={1} sx={{ flex: 1, overflow: 'auto', maxHeight: 460 }}>
            {unplacedTables.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  color: FP.faint,
                }}
              >
                <TableRestaurantOutlinedIcon sx={{ opacity: 0.5 }} />
                <Typography variant="body2" sx={{ color: FP.sub, mt: 0.5 }}>
                  All section tables are on the floor.
                </Typography>
              </Box>
            ) : (
              unplacedTables.map((table) => {
                const uuid = table.tableDetails.uuid;
                const name =
                  table.tableDetails.displayName ||
                  table.tableDetails.name ||
                  'Table';
                return (
                  <Box
                    key={uuid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: FP.surface,
                      border: `1px solid ${FP.lineSoft}`,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: FP.faint,
                        boxShadow: FP.shadowSm,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <TableShapeGlyph
                        shape={table.tableDetails.shape || 'square'}
                        width={24}
                        height={24}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: FP.ink,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: FP.sub }}>
                          {table.tableDetails.capacity || 0} seats
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fp.placeCatalogTable(table)}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1.5 }}
                    >
                      Place
                    </Button>
                  </Box>
                );
              })
            )}
          </Stack>
        </Box>

        {/* Center canvas */}
        <FloorPlanCanvas
          layout={fp.sectionLayout}
          tables={fp.sectionTables}
          sectionUuid={fp.selectedSectionUuid}
          selectedTableUuid={fp.selectedTableUuid}
          selectedObjectId={fp.selectedObjectId}
          onSelectTable={fp.setSelectedTableUuid}
          onSelectObject={fp.setSelectedObjectId}
          onUpdateTablePlacement={fp.updateTablePlacement}
          onUpdateFloorObject={fp.updateFloorObject}
          snapshotHistory={fp.snapshotHistory}
          outlineEdit={outlineEdit}
          onUpdateOutline={fp.updateSectionOutline}
          onFillToViewport={handleFillToViewport}
        />

        {/* Right inspector */}
        <Box
          sx={{
            ...fpCard,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <FloorPlanInspector
            sections={fp.sections}
            sectionUuid={fp.selectedSectionUuid}
            selectedTable={selectedTable}
            selectedPlacement={selectedPlacement}
            selectedObject={selectedObject}
            onUpdateCatalog={fp.updateTableCatalog}
            onUpdatePlacement={fp.updateTablePlacement}
            onMoveSection={fp.moveTableToSection}
            onRotate={fp.rotateSelectedTable}
            onDuplicate={fp.duplicateSelectedTable}
            onDeleteTable={fp.removeTable}
            onUpdateObject={fp.updateFloorObject}
            onDeleteObject={fp.removeFloorObject}
            onRemoveFromFloor={fp.removeFromFloor}
          />
        </Box>
      </Box>

      <Fab
        color="primary"
        size="medium"
        onClick={(e) => setAddMenuAnchor(e.currentTarget)}
        sx={{
          position: 'fixed',
          right: 28,
          bottom: 28,
          bgcolor: FP.primary,
          boxShadow: '0 10px 24px rgba(45,127,249,0.40)',
          '&:hover': { bgcolor: FP.primaryDark },
          zIndex: 20,
        }}
      >
        <AddIcon />
      </Fab>
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        PaperProps={{
          sx: { borderRadius: 2.5, boxShadow: FP.shadowMd, minWidth: 180 },
        }}
      >
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setShowAddTable(true);
          }}
        >
          Add table
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setShowAddObject(true);
          }}
        >
          Add object
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setShowAddSection(true);
          }}
        >
          Add section
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setShowHall(true);
          }}
        >
          Floor color
        </MenuItem>
      </Menu>

      <AddTableModal
        open={showAddTable}
        onClose={() => setShowAddTable(false)}
        presets={fp.presets}
        layoutTemplates={fp.layoutTemplates}
        onAdd={async (preset, count) => {
          if (count > 1) {
            await fp.addTablesFromPreset(preset, count);
          } else {
            await fp.addTableFromPreset(preset);
          }
        }}
        onAddPack={fp.applyLayoutTemplate}
      />
      <AddObjectModal
        open={showAddObject}
        onClose={() => setShowAddObject(false)}
        presets={fp.objectPresets}
        onAdd={fp.addFloorObject}
      />
      <AddSectionModal
        open={showAddSection}
        onClose={() => setShowAddSection(false)}
        onCreate={fp.createSection}
      />
      <ArrangeSheet
        open={showArrange}
        onClose={() => setShowArrange(false)}
        tableCount={placedCount}
        onArrangeAuto={() => fp.arrangeTablesInHallShape()}
        onArrangeGrid={(opts) => fp.arrangeTablesInHallShape(opts)}
        onApplyArrangement={fp.applyArrangement}
      />
      <HallStyleSheet
        open={showHall}
        onClose={() => setShowHall(false)}
        style={fp.sectionLayout?.style}
        onUpdateStyle={fp.updateHallStyle}
        onSnapshotHistory={fp.snapshotHistory}
      />
    </Box>
  );
};

export default FloorPlanBuilder;
